/**
 * Badge del estado de una regularización (007 · T036/T056/T057; contracts/ui.md §1.3,
 * design-system §7 "Badge de estado"): píldora con icono **y** texto, nunca solo color.
 *
 * | Estado | Tono | Icono |
 * |---|---|---|
 * | `SUGGESTED` "Por regularizar" | Advertencia | `clock-alert` |
 * | `ASKED` "Te lo pidieron" | Advertencia | `message-square` |
 * | `PENDING` "En revisión" | Info | `hourglass` |
 * | `APPROVED` "Aprobada" | Éxito | `check` |
 * | `REJECTED` "Rechazada" | Error | `x-circle` |
 * | `CANCELLED` "Cancelada" | Neutro | `minus-circle` |
 *
 * En Staff el texto del badge va a 14 px (mínimo de metadatos de §8.1), no a los 12 px del Admin.
 */
import {
  Check,
  CircleMinus,
  CircleX,
  ClockAlert,
  Hourglass,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';

import type { StaffRegularizationState } from '@fermin/contracts';

import { cn } from '../../components/ui/cn';
import { t } from '../../i18n';

export const STATE_ICON: Record<StaffRegularizationState, LucideIcon> = {
  SUGGESTED: ClockAlert,
  ASKED: MessageSquare,
  PENDING: Hourglass,
  APPROVED: Check,
  REJECTED: CircleX,
  CANCELLED: CircleMinus,
};

const STATE_TONE: Record<StaffRegularizationState, string> = {
  SUGGESTED: 'bg-fm-warning-soft text-fm-warning-text',
  ASKED: 'bg-fm-warning-soft text-fm-warning-text',
  PENDING: 'bg-fm-info-soft text-fm-info-text',
  APPROVED: 'bg-fm-success-soft text-fm-success-text',
  REJECTED: 'bg-fm-danger-soft text-fm-danger-text',
  CANCELLED: 'bg-fm-bg-sunken text-fm-text',
};

export function stateLabel(state: StaffRegularizationState): string {
  return t(`regularization.states.${state}`);
}

export interface StateBadgeProps {
  state: StaffRegularizationState;
  className?: string;
  'data-testid'?: string;
}

export function StateBadge({ state, className, 'data-testid': testId }: StateBadgeProps) {
  const Icon = STATE_ICON[state];
  return (
    <span
      data-testid={testId ?? 'regularization-badge'}
      data-state={state}
      className={cn(
        'inline-flex w-fit items-center gap-fm-1 rounded-fm-pill px-fm-2 py-0.5 font-ui text-fm-sm font-medium',
        STATE_TONE[state],
        className,
      )}
    >
      <Icon aria-hidden="true" size={14} />
      {stateLabel(state)}
    </span>
  );
}
